/** Small pieces shared by the sessions screens. */
import { useEffect } from "react";
import { can, type EventTypeDef, type Formatters, type JobDetail, type Ref } from "../../../contract";
import { plural, t } from "../../../web/lib/i18n";
import { useMe } from "../../../web/lib/session";
import { registry } from "../../../web/registry";
import { formatValue } from "../../../web/shell/describe";
import { Chip } from "../../../web/ui/kit";
import { knownDefaults } from "../shared/template";
import type { RosterEntry, SessionCounts, SessionSetup, StepDef } from "../shared/types";
import { startSessionOutbox, useOnline, useQueued, useReceipts, useSyncState } from "./offline";

export const lookup = (type: string): EventTypeDef | undefined => registry.event(type);

/** Scope the device outbox to whoever is signed in. Called by every sessions screen and the Today card. */
export function useSessionsOwner(): void {
  const { data: me } = useMe();
  useEffect(() => {
    if (me?.ranch && me.membership) startSessionOutbox(me.ranch.id, me.membership.id, can(me.membership.role, "read"));
  }, [me?.ranch?.id, me?.membership?.id, me?.membership?.role]);
}

/** A step's name at the chute: the crew's own label, or the record type's. */
export function stepTitle(step: StepDef): string {
  if (step.label) return step.label;
  const def = lookup(step.type);
  return def ? t(def.label) : step.type;
}

/** "Vision 7 with Spur · 2 mL · Under the skin" from a step's shared defaults. */
export function defaultsLine(step: StepDef, fmt: Formatters): string {
  const def = lookup(step.type);
  if (!def) return "";
  return Object.entries(knownDefaults(step, def)).map(([k, v]) => formatValue(def.fields[k], v, fmt)).filter(Boolean).join(" · ");
}

export interface SessionDetail {
  job: JobDetail;
  setup: SessionSetup;
  counts: SessionCounts;
  roster: Array<RosterEntry & { label: string }>;
  labels: Record<string, string>;
}

export function statusChip(status: RosterEntry["status"] | "saved" | "conflict") {
  switch (status) {
    case "done": return <Chip tone="good" icon="done">{t("Worked")}</Chip>;
    case "skipped": return <Chip tone="neutral" icon="skip">{t("Skipped")}</Chip>;
    case "exception": return <Chip tone="warn" icon="warn">{t("Needs a look")}</Chip>;
    case "saved": return <Chip tone="info" icon="phone">{t("Saved on this phone")}</Chip>;
    case "conflict": return <Chip tone="warn" icon="warn">{t("Needs a look")}</Chip>;
    default: return <Chip tone="neutral">{t("To do")}</Chip>;
  }
}

/** Plain words for where this device's work stands: saved here, sending, in ranch records, needs a look. */
export function SyncStatus({ jobId, compact }: { jobId?: string; compact?: boolean }) {
  const queued = useQueued(jobId);
  const receipts = useReceipts(jobId);
  const online = useOnline();
  const sync = useSyncState();
  const waiting = queued.filter((q) => q.state === "saved" || q.state === "sending").length;
  const blocked = queued.filter((q) => q.state === "blocked" || q.state === "failed").length;
  const conflicts = receipts.filter((r) => r.status === "conflict").length;
  if (blocked) return <Chip tone="bad" icon="warn">{plural(blocked, "{{count}} can't be sent", "{{count}} can't be sent")}</Chip>;
  if (waiting && !online) return <Chip tone="warn" icon="offline">{compact ? plural(waiting, "{{count}} on this phone", "{{count}} on this phone") : plural(waiting, "Offline · {{count}} saved on this phone", "Offline · {{count}} saved on this phone")}</Chip>;
  if (waiting && sync.lastStop === "auth") return <Chip tone="warn" icon="warn">{t("Sign in again to send")}</Chip>;
  if (waiting) return <Chip tone="info" icon="cloudUp">{sync.sending ? plural(waiting, "Sending {{count}}", "Sending {{count}}") : plural(waiting, "{{count}} saved on this phone", "{{count}} saved on this phone")}</Chip>;
  if (conflicts && !compact) return <Chip tone="warn" icon="warn">{plural(conflicts, "{{count}} needs a look", "{{count}} need a look")}</Chip>;
  return <Chip tone="good" icon="done">{compact ? t("Sent") : t("All in ranch records")}</Chip>;
}

/** An animal's status as a word in a sentence ("marked sold"). */
export function statusWord(status: string): string {
  switch (status) {
    case "sold": return t("sold");
    case "dead": return t("dead");
    case "culled": return t("culled");
    case "transferred": return t("transferred");
    case "missing": return t("missing");
    case "reference": return t("reference only");
    default: return t("in the herd");
  }
}

export function sexWord(sex: string): string {
  return sex === "female" ? t("Female") : sex === "male" ? t("Male") : t("Unknown sex");
}

/** The breeding module's own word for a status when it publishes one, ours otherwise. */
export function breedingWord(status: string): string {
  const own = registry.facets.find((f) => f.id === "breeding" && f.subject === "animal")?.filters?.status?.options?.[status];
  if (own) return t(own);
  switch (status) {
    case "open": return t("Open (not pregnant)");
    case "exposed": return t("Exposed to a bull");
    case "bred": return t("Bred, not checked yet");
    case "pregnant": return t("Pregnant (checked)");
    case "calved": return t("Calved, not bred again yet");
    default: return t("Breeding unknown");
  }
}

export function crewLine(crew: string[] | undefined, labels: Record<string, string>): string {
  return (crew ?? []).map((id) => labels[id]).filter(Boolean).join(", ");
}

export type { Ref };
